import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import MergeTypeIcon from '@mui/icons-material/MergeType';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { BaseCatalogItem, CatalogItemConfig } from '~/components/Accommodation/types/catalogItem';
import Table from '~/components/v2/common/Table/Table';

const PAGE_SIZE = 25;

interface MergeCatalogItemModalProps<
  TItem extends BaseCatalogItem,
  TUpdatePayload,
  TCreatePayload,
  TApprovePayload,
  TMergePayload,
> {
  open: boolean;
  onClose: () => void;
  draftItem: TItem | null;
  onMerge: (draftCode: string, existingCode: string) => void;
  isMerging: boolean;
  config: CatalogItemConfig<TItem, TUpdatePayload, TCreatePayload, TApprovePayload, TMergePayload>;
}

export default function MergeCatalogItemModal<
  TItem extends BaseCatalogItem,
  TUpdatePayload,
  TCreatePayload,
  TApprovePayload,
  TMergePayload,
>({
  open,
  onClose,
  draftItem,
  onMerge,
  isMerging,
  config,
}: MergeCatalogItemModalProps<TItem, TUpdatePayload, TCreatePayload, TApprovePayload, TMergePayload>) {
  const [page, setPage] = useState(0);
  const [searchName, setSearchName] = useState('');

  const {
    data: itemsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accommodation', config.queryKeyPrefix, 'approved', { page: page + 1, searchName }],
    queryFn: async () => {
      const response = await config.listItems({
        name: searchName || undefined,
        status: 'approved',
        limit: String(PAGE_SIZE),
        page: String(page + 1),
      });
      return response.result;
    },
    enabled: open,
  });

  const items = itemsData?.items ?? [];
  const totalCount = itemsData?.total ?? 0;

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
  };

  const columns: Array<GridColDef<TItem>> = [
    {
      field: 'code',
      headerName: 'Code',
      width: 180,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'aliases',
      headerName: 'Aliases',
      width: 250,
      renderCell: (params: GridRenderCellParams<TItem>) => (
        <Typography variant="body2" noWrap title={params.value?.join(', ') ?? ''}>
          {params.value?.join(', ') ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'catalogGroup',
      headerName: 'Group',
      width: 150,
      renderCell: (params: GridRenderCellParams<TItem>) => (
        <Typography variant="body2">{params.value?.name ?? '-'}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<TItem>) => (
        <Tooltip title={`Merge "${draftItem?.name}" into "${params.row.name}"`}>
          <span>
            <IconButton
              size="small"
              onClick={() => {
                if (draftItem) {
                  onMerge(draftItem.code, params.row.code);
                }
              }}
              disabled={isMerging}
              color="primary"
            >
              {isMerging ? <CircularProgress size={18} /> : <MergeTypeIcon />}
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Merge Draft {config.itemName}
        {draftItem && (
          <Typography variant="body2" color="textSecondary">
            Select an approved {config.itemName.toLowerCase()} to merge &quot;{draftItem.name}&quot; ({draftItem.code})
            into
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading {config.itemNamePlural.toLowerCase()}:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2, mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Search by name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button variant="contained" type="submit">
              Search
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearchName('');
                setPage(0);
              }}
            >
              Clear
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Merging will add the draft&apos;s aliases into the aliases of the selected {config.itemName.toLowerCase()}.
          {config.type !== 'inclusion' &&
            ` Associated ${config.type === 'facility' ? 'properties' : 'rooms'} will be linked to the selected ${config.itemName.toLowerCase()}.`}
        </Typography>

        <Table
          columns={columns}
          rows={items}
          getRowId={(row) => row.code}
          paginationMode="server"
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          onPaginationModelChange={(model) => setPage(model.page)}
          totalRowCount={totalCount}
          isLoading={isLoading}
          isDisabled
          autoHeight
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isMerging}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
